"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { InputWrapper } from "@/components/forms/InputWrapper";
import { Button } from "@/components/buttons/Button";
import { Icon } from "@/components/icons/Icon";
import { Logo } from "@/components/Logo";
import { EmailInput, isValidEmail } from "@/components/forms/EmailInput";
import { OtpInput } from "@/components/forms/OtpInput";
import ErrorMessage from "@/components/ErrorMessage";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [session, setSession] = useState("");
    const [step, setStep] = useState<"email" | "otp">("email");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [failure, setFailure] = useState(false);

    const handleEmailSubmit = async () => {
        if (!isValidEmail(email)) return;

        setLoading(true);
        setError("");

        try {
            const response = await fetch("/api/auth/otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "send", email }),
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setSession(data.session);
                setStep("otp");
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            if (
                err instanceof Error &&
                err.message.includes("User not found")
            ) {
                setEmail("");
                setFailure(true);
            } else {
                setError("חלה שגיאה בשליחת קוד אימות - נסה לשלוח קוד שוב");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOtpSubmit = async () => {
        if (!otp) return;

        setLoading(true);
        setError("");

        try {
            const response = await fetch("/api/auth/otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "verify", email, otp, session }),
            });
            const data = await response.json();
            if (response.ok && data.success) {
                const signInResult = await signIn("credentials", {
                    idToken: data.tokens.idToken,
                    accessToken: data.tokens.accessToken,
                    refreshToken: data.tokens.refreshToken,
                    redirect: false,
                });
                if (signInResult?.ok) {
                    window.location.href = "/";
                    return;
                } else {
                    throw new Error("Sign in failed");
                }
            } else {
                throw new Error();
            }
        } catch {
            setError("הקוד שהוזן שגוי או פג תוקף - נסה שנית");
        } finally {
            setLoading(false);
        }
    };

    const handleBackToEmail = () => {
        setStep("email");
        setOtp("");
        setSession("");
        setError("");
    };

    const showHeader = step === "email" || failure;

    return (
        <div className="w-full flex items-center justify-center">
            <div
                className={`w-full max-w-[400px] flex flex-col items-center justify-center gap-8 px-1 ${
                    showHeader ? "pt-8" : "pt-0"
                } "pb-8"`}
            >
                {showHeader ? (
                    <Logo />
                ) : (
                    <div className="w-full flex justify-end stand-alone-logo">
                        <Icon
                            name="logo"
                            width={24}
                            height={24}
                            transition={false}
                            priority
                        />
                    </div>
                )}
                {failure ? (
                    <ErrorMessage
                        title="ההתחברות נכשלה"
                        message={[
                            "טרם נפתחה לך הרשאה למערכת.",
                            'אנא פנה למנהל.ת ביה"ס לבירור.',
                        ]}
                        buttonLabel="חזרה למסך הראשי"
                        onButtonClick={() => setFailure(false)}
                    />
                ) : (
                    <div className="w-full flex flex-col items-start gap-4">
                        {step === "email" ? (
                            <>
                                <div className="w-full flex flex-col items-center">
                                    <Icon
                                        name="airplane"
                                        width={123}
                                        height={144}
                                        transition={false}
                                        priority
                                    />
                                </div>
                                <div className="w-full flex flex-col items-start gap-2">
                                    <h1>ברוכים הבאות.ים</h1>
                                    <h4>לחוויה לימודים בלתי נשכחת</h4>
                                </div>
                            </>
                        ) : (
                            <div className="w-full flex flex-col items-start gap-2 desktop-align-items-center">
                                <h1>הקוד אצלך בדוא&quot;ל!</h1>
                                <h4>שלחנו את הקוד אל {email}</h4>
                            </div>
                        )}
                        <form>
                            {step === "email" ? (
                                <InputWrapper
                                    label='כתובת דוא"ל לקבלת קוד חד פעמי'
                                    validationErrorMessage={
                                        error ||
                                        (email && !isValidEmail(email)
                                            ? `כתובת דוא"ל לא תקנית`
                                            : undefined)
                                    }
                                >
                                    <EmailInput
                                        id="email"
                                        name="email"
                                        required={true}
                                        placeholder="example@mail.com"
                                        value={email}
                                        onChange={(newValue) =>
                                            setEmail(newValue)
                                        }
                                        disabled={loading}
                                    />
                                </InputWrapper>
                            ) : (
                                <InputWrapper
                                    label="קוד חד פעמי"
                                    validationErrorMessage={error}
                                >
                                    <OtpInput
                                        numInputs={8}
                                        value={otp}
                                        onChange={setOtp}
                                        isError={!!error}
                                    />
                                </InputWrapper>
                            )}

                            <div className="flex flex-col gap-2">
                                {step === "email" ? (
                                    <Button
                                        variant="secondary"
                                        icon="envelope"
                                        onClick={handleEmailSubmit}
                                        disabled={
                                            loading || !isValidEmail(email)
                                        }
                                    >
                                        {loading
                                            ? "שולח קוד..."
                                            : `שלח קוד לדוא"ל`}
                                    </Button>
                                ) : (
                                    <div className="flex flex-col gap-4">
                                        <Button
                                            variant="primary"
                                            icon="left"
                                            onClick={handleOtpSubmit}
                                            disabled={loading || !otp}
                                        >
                                            {loading ? "מתחבר..." : "התחבר"}
                                        </Button>

                                        <Button
                                            variant="secondary"
                                            icon="sync"
                                            onClick={handleBackToEmail}
                                        >
                                            חזור לשלב הקודם
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
